import { useState, useCallback } from 'react';
import { Loader2, Eye, AlertTriangle, CheckCircle2, Database, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const ENTITY_OPTIONS = [
  { value: 'afpv', label: 'AFPV' },
  { value: 'bi_immo', label: 'BI Immobilier' },
];

export function BackfillExternalResIdPanel() {
  const [entityId, setEntityId] = useState('afpv');
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const canApply = preview && !applying && preview.safe_to_apply;

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.backfillExternalResIdPreview({
        entity_id: entityId,
      });
      setPreview(data);
      if (!data.safe_to_apply && data.candidates_found > 0) {
        toast.error('Overlap proof failed. Do not apply.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, [entityId]);

  const runApply = useCallback(async () => {
    setApplying(true);
    try {
      const { data } = await diagnosticsAPI.backfillExternalResIdApply();
      setResult(data);
      setPreview(null);
      if (data.updated > 0) {
        toast.success(`Backfill complete: ${data.updated} reservation(s) updated.`);
      }
      if (data.flagged_conflict > 0) {
        toast.error(`${data.flagged_conflict} conflict(s) flagged. Check results.`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-500" />
          Backfill external_reservation_id (Booking.com)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Historical Booking.com reservations may have an empty external_reservation_id,
          preventing channel-ref matching against bank payout labels. This backfills the
          field by re-fetching from the Uplisting API. Preview proves overlap with bank
          ref: tokens (fetched from Pennylane) before any write.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground whitespace-nowrap" htmlFor="bf-entity">
              Pennylane entity
            </label>
            <select
              id="bf-entity"
              value={entityId}
              onChange={(e) => { setEntityId(e.target.value); setPreview(null); setResult(null); }}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              {ENTITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <Button onClick={runPreview} disabled={previewing} variant="default" size="sm">
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview Backfill
          </Button>
          <Button
            variant="destructive"
            onClick={runApply}
            disabled={!canApply}
            size="sm"
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Apply Backfill
          </Button>
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-4">
            {/* Verdict banner */}
            <div className={`rounded-lg border p-3 ${
              preview.safe_to_apply
                ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center gap-2">
                {preview.safe_to_apply ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
                )}
                <span className={`text-sm font-medium ${
                  preview.safe_to_apply
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}>
                  {preview.verdict}
                </span>
              </div>
            </div>

            {/* Bank ref error (credential issue) */}
            {preview.bank_ref_error && (
              <div className="rounded-lg border p-3 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800">
                <span className="text-sm text-yellow-800 dark:text-yellow-300">
                  {preview.bank_ref_error}
                </span>
              </div>
            )}

            {/* Summary counts */}
            <div className="rounded-lg border p-3 bg-muted/30 space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Preview (read-only, no writes)</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Candidates</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.candidates_found}
                  </div>
                  <div className="text-[10px] text-muted-foreground">missing ext_res_id</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Fetched (populated)</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.fetched_populated}
                  </div>
                  <div className="text-[10px] text-muted-foreground">from Uplisting API</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Fetched (empty)</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.fetched_empty}
                  </div>
                  <div className="text-[10px] text-muted-foreground">API returned no value</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Bank refs</div>
                  <div className="text-lg font-bold tabular-nums">
                    {preview.bank_refs_found}
                  </div>
                  <div className="text-[10px] text-muted-foreground">Booking.com ref: tokens</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Overlap</div>
                  <div className={`text-lg font-bold tabular-nums ${
                    preview.overlap_count > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {preview.overlap_count}
                  </div>
                  <div className="text-[10px] text-muted-foreground">fetched == bank ref</div>
                </div>
              </div>
            </div>

            {/* Example matched pairs */}
            {preview.examples?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Matched examples (fetched value == bank ref token)
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Uplisting ID</th>
                        <th className="py-1 pr-3 font-medium">Fetched ext_res_id</th>
                        <th className="py-1 pr-3 font-medium">Guest</th>
                        <th className="py-1 pr-3 font-medium">Check-in</th>
                        <th className="py-1 pr-3 font-medium">Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.examples.map((ex, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{ex.uplisting_id}</td>
                          <td className="py-1 pr-3 font-mono font-medium text-green-700 dark:text-green-400">
                            {ex.fetched_external_reservation_id}
                          </td>
                          <td className="py-1 pr-3">{ex.guest_name}</td>
                          <td className="py-1 pr-3">{ex.check_in}</td>
                          <td className="py-1 pr-3">{ex.property_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Full candidate details */}
            {preview.candidate_details?.length > 0 && (
              <details className="space-y-2">
                <summary className="text-sm font-semibold cursor-pointer">
                  All candidates ({preview.candidate_details.length})
                </summary>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Uplisting ID</th>
                        <th className="py-1 pr-3 font-medium">Property</th>
                        <th className="py-1 pr-3 font-medium">Guest</th>
                        <th className="py-1 pr-3 font-medium">Check-in</th>
                        <th className="py-1 pr-3 font-medium">Fetched value</th>
                        <th className="py-1 pr-3 font-medium">Bank ref?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.candidate_details.map((cd, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{cd.uplisting_id}</td>
                          <td className="py-1 pr-3">{cd.property_name}</td>
                          <td className="py-1 pr-3">{cd.guest_name}</td>
                          <td className="py-1 pr-3">{cd.check_in}</td>
                          <td className="py-1 pr-3 font-mono">
                            {cd.fetched_value || <span className="text-muted-foreground italic">empty</span>}
                          </td>
                          <td className="py-1 pr-3">
                            {cd.fetched_value ? (
                              cd.matches_bank_ref ? (
                                <Badge variant="default" className="text-[10px] bg-green-600">yes</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px]">no</Badge>
                              )
                            ) : (
                              <span className="text-muted-foreground">n/a</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}

        {/* Apply results */}
        {result && (
          <div className="rounded-lg border p-3 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-800 dark:text-green-300">
                Backfill applied
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Updated</div>
                <div className="text-lg font-bold tabular-nums text-green-600">{result.updated}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Already populated</div>
                <div className="text-lg font-bold tabular-nums">{result.skipped_already_populated}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Conflicts</div>
                <div className={`text-lg font-bold tabular-nums ${result.flagged_conflict > 0 ? 'text-red-600' : ''}`}>
                  {result.flagged_conflict}
                </div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">No API value</div>
                <div className="text-lg font-bold tabular-nums">{result.failed_no_value}</div>
              </div>
            </div>

            {/* Per-reservation results */}
            {result.results?.length > 0 && (
              <details className="space-y-2">
                <summary className="text-sm font-semibold cursor-pointer">
                  Per-reservation results ({result.results.length})
                </summary>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-1 pr-3 font-medium">Uplisting ID</th>
                        <th className="py-1 pr-3 font-medium">Guest</th>
                        <th className="py-1 pr-3 font-medium">Action</th>
                        <th className="py-1 pr-3 font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((r, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1 pr-3 font-mono">{r.uplisting_id}</td>
                          <td className="py-1 pr-3">{r.guest_name}</td>
                          <td className="py-1 pr-3">
                            <Badge
                              variant={
                                r.action === 'updated' ? 'default' :
                                r.action === 'conflict' ? 'destructive' : 'outline'
                              }
                              className="text-[10px]"
                            >
                              {r.action}
                            </Badge>
                          </td>
                          <td className="py-1 pr-3 font-mono">
                            {r.value || '-'}
                            {r.existing_value && (
                              <span className="text-red-600 ml-1">(existing: {r.existing_value})</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
